// useVoiceCall.js 원본과 동일 복사본 — 1:1 파일 무변경 원칙, 출시 후 통합 예정

const CALL_AUDIO_CONSTRAINTS = Object.freeze({ audio: true, video: false });

/**
 * 마이크 교체 시 선호 제약 조건 조회 헬퍼
 * - 기본 장치(deviceId: 'default')가 목록에 있으면 exact: 'default' 지정
 * - 없거나 enumerateDevices 실패 시 기존 CALL_AUDIO_CONSTRAINTS 반환
 * - 장치 이름(label)은 보지 않음 (규칙 22 준수)
 */
async function getPreferredMicConstraints() {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
    return CALL_AUDIO_CONSTRAINTS;
  }
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const hasDefault = devices.some((d) => d.kind === 'audioinput' && d.deviceId === 'default');
    if (hasDefault) {
      return { audio: { deviceId: { exact: 'default' } }, video: false };
    }
  } catch {}
  return CALL_AUDIO_CONSTRAINTS;
}

/**
 * 통화 시작(발신/수신) 마이크 스트림 획득 헬퍼
 * - OS 기본 장치(default) 우선 시도 및 실패 시 기본 조건 재시도
 */
export async function acquireGroupMic() {
  const constraints = await getPreferredMicConstraints();
  try {
    return await navigator.mediaDevices.getUserMedia(constraints);
  } catch (e) {
    if (constraints !== CALL_AUDIO_CONSTRAINTS &&
        (e?.name === 'OverconstrainedError' || e?.name === 'NotFoundError')) {
      if (import.meta.env.DEV) console.log('[마이크 잡음] default 지정 실패 → 기본 방식 재시도:', e.name);
      return await navigator.mediaDevices.getUserMedia(CALL_AUDIO_CONSTRAINTS);
    }
    throw e;
  }
}

let groupSinkId = null;

/**
 * 단체 통화 상대방 원격 오디오 스트림 재생용 숨은 <audio> 엘리먼트 연결
 * - document.body에 부착하여 컴포넌트 언마운트와 무관하게 소리 유지
 * - 동일 peerId 엘리먼트가 이미 있으면 재사용
 * - 자동재생 차단(NotAllowedError) 시 onBlocked 콜백 호출
 */
export function attachGroupRemoteAudio(peerId, stream, onBlocked) {
  if (!peerId || !stream || typeof document === 'undefined') return null;

  let audio = document.querySelector(`audio[data-group-peer="${peerId}"]`);
  if (!audio) {
    audio = document.createElement('audio');
    audio.autoplay = true;
    audio.playsInline = true;
    audio.style.display = 'none';
    audio.dataset.groupPeer = peerId;
    document.body.appendChild(audio);
    if (groupSinkId && typeof audio.setSinkId === 'function') {
      audio.setSinkId(groupSinkId).catch((e) => console.warn('[단체 통화] 출력 장치 적용 실패 —', e?.name || e));
    }
  }

  audio.srcObject = stream;
  audio.muted = false;

  audio.play().catch((err) => {
    console.warn(`[단체 오디오] peer ${peerId?.slice?.(0, 8) || peerId} 재생 실패:`, err);
    if (err?.name === 'NotAllowedError') {
      onBlocked?.();
    }
  });

  return audio;
}

/**
 * 특정 상대방의 오디오 엘리먼트 정지 및 제거
 */
export function removeGroupRemoteAudio(peerId) {
  if (!peerId || typeof document === 'undefined') return;
  const audio = document.querySelector(`audio[data-group-peer="${peerId}"]`);
  if (audio) {
    try {
      audio.pause();
      audio.srcObject = null;
      audio.remove();
    } catch (e) {
      console.warn(`[단체 오디오] peer ${peerId?.slice?.(0, 8) || peerId} 제거 실패:`, e);
    }
  }
}

/**
 * 모든 단체 통화 오디오 엘리먼트 일괄 정지 및 제거
 */
export function removeAllGroupRemoteAudio() {
  if (typeof document === 'undefined') return;
  const audios = document.querySelectorAll('audio[data-group-peer]');
  audios.forEach((audio) => {
    try {
      audio.pause();
      audio.srcObject = null;
      audio.remove();
    } catch (e) {
      console.warn('[단체 오디오] 일괄 제거 실패:', e);
    }
  });
}

/**
 * 모든 단체 통화 오디오 엘리먼트 재생 재시도 (소리 켜기 터치 시 호출)
 */
export async function resumeGroupRemoteAudio() {
  if (typeof document === 'undefined') return;
  const audios = document.querySelectorAll('audio[data-group-peer]');
  for (const audio of audios) {
    try {
      await audio.play();
    } catch {}
  }
}

export async function setGroupOutputDevice(deviceId) {
  const els = typeof document !== 'undefined'
    ? Array.from(document.querySelectorAll('audio[data-group-peer]'))
    : [];
  if (els.some((el) => typeof el.setSinkId !== 'function')) {
    throw new Error('setSinkId 미지원');
  }
  const prev = groupSinkId ?? '';
  const done = [];
  try {
    for (const el of els) {
      await el.setSinkId(deviceId);
      done.push(el);
    }
    groupSinkId = deviceId;
  } catch (err) {
    for (const el of done) {
      await el.setSinkId(prev).catch(() => {});
    }
    throw err;
  }
}

export function resetGroupOutputDevice() {
  groupSinkId = null;
}

export async function acquireGroupCamera() {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    const err = new Error('getUserMedia not supported');
    err.name = 'NotSupportedError';
    throw err;
  }
  return await navigator.mediaDevices.getUserMedia({
    audio: false,
    video: {
      facingMode: 'user',
      width: { ideal: 640 },
      height: { ideal: 480 },
      frameRate: { ideal: 24 },
    },
  });
}

export function stopGroupCamera(stream) {
  if (!stream) return;
  try {
    stream.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch {}
    });
  } catch {}
}
